import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AccountShell, Panel } from "@/components/site/AccountShell";
import { FieldError, fieldErrors, Loading, Notice, ProblemAlert } from "@/components/site/Feedback";
import { PhoneInput } from "@/components/site/PhoneInput";
import { api } from "@/lib/api";
import { refreshTokens } from "@/lib/api";
import { DEFAULT_COUNTRY_CODE, normalizePhone, splitPhone, validatePhone } from "@/lib/phone";
import type { ProfileResponse } from "@/lib/types";

export function ProfilePage() {
  usePageTitle("Profile");
  const profile = useQuery({ queryKey: ["account", "profile"], queryFn: api.account.profile });

  return (
    <AccountShell title="Your profile" subtitle="Personal details, password and sign-in email.">
      {profile.isPending ? (
        <Loading />
      ) : profile.isError ? (
        <ProblemAlert error={profile.error} />
      ) : (
        <div className="grid gap-8 lg:grid-cols-2">
          <DetailsForm profile={profile.data} />
          <div className="space-y-8">
            <PasswordForm />
            <EmailForm current={profile.data.email} />
            <Panel title="Delete account">
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and personal data. Active memberships are cancelled.
              </p>
              <Button asChild variant="outline" size="sm" className="mt-4 border-destructive/40 text-destructive hover:bg-destructive/5">
                <Link to="/account/delete">Delete my account</Link>
              </Button>
            </Panel>
          </div>
        </div>
      )}
    </AccountShell>
  );
}

function DetailsForm({ profile }: { profile: ProfileResponse }) {
  const queryClient = useQueryClient();
  const initial = splitPhone(profile.phoneNumber);
  const [firstName, setFirstName] = useState(profile.firstName);
  const [lastName, setLastName] = useState(profile.lastName);
  const [countryCode, setCountryCode] = useState(profile.phoneNumber ? initial.countryCode : DEFAULT_COUNTRY_CODE);
  const [national, setNational] = useState(profile.phoneNumber ? initial.national : "");

  useEffect(() => {
    setFirstName(profile.firstName);
    setLastName(profile.lastName);
  }, [profile.firstName, profile.lastName]);

  const phoneError = validatePhone(countryCode, national, false);
  const save = useMutation({
    mutationFn: () =>
      api.account.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: normalizePhone(countryCode, national),
        preferredCulture: profile.preferredCulture ?? "en",
      }),
    onSuccess: async () => {
      toast.success("Your details were saved");
      // New name claims in the access token.
      await refreshTokens();
      void queryClient.invalidateQueries({ queryKey: ["account"] });
      void queryClient.invalidateQueries({ queryKey: ["billing", "overview"] });
    },
  });

  return (
    <Panel title="Personal details" className="h-fit">
      <form
        className="space-y-5"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!phoneError) save.mutate();
        }}
      >
        <ProblemAlert error={save.error} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">First name</Label>
            <Input id="firstName" required maxLength={100} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <FieldError messages={fieldErrors(save.error, "FirstName")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last name</Label>
            <Input id="lastName" required maxLength={100} value={lastName} onChange={(e) => setLastName(e.target.value)} />
            <FieldError messages={fieldErrors(save.error, "LastName")} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email-ro">Email</Label>
          <Input id="email-ro" value={profile.email} readOnly disabled />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone number (optional)</Label>
          <PhoneInput
            id="phone"
            countryCode={countryCode}
            national={national}
            invalid={!!phoneError}
            onChange={(c, n) => {
              setCountryCode(c);
              setNational(n);
            }}
          />
          <FieldError messages={[phoneError, ...(fieldErrors(save.error, "PhoneNumber") ?? [])]} />
        </div>
        <Button type="submit" variant="gold" disabled={save.isPending || !firstName.trim() || !lastName.trim()}>
          {save.isPending && <Loader2 className="animate-spin" />}
          Save details
        </Button>
      </form>
    </Panel>
  );
}

function PasswordForm() {
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
  const mismatch = form.confirmNewPassword && form.newPassword !== form.confirmNewPassword ? "The passwords do not match." : null;
  const change = useMutation({
    mutationFn: () => api.account.changePassword(form),
    onSuccess: () => {
      toast.success("Your password was changed");
      setForm({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
    },
  });
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Panel title="Change password">
      <form
        className="space-y-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) change.mutate();
        }}
      >
        <ProblemAlert error={change.error} />
        <div className="space-y-2">
          <Label htmlFor="currentPassword">Current password</Label>
          <Input id="currentPassword" type="password" autoComplete="current-password" value={form.currentPassword} onChange={set("currentPassword")} />
          <FieldError messages={fieldErrors(change.error, "CurrentPassword")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="newPassword">New password</Label>
          <Input id="newPassword" type="password" autoComplete="new-password" value={form.newPassword} onChange={set("newPassword")} />
          <FieldError messages={fieldErrors(change.error, "NewPassword")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmNewPassword">Confirm new password</Label>
          <Input id="confirmNewPassword" type="password" autoComplete="new-password" value={form.confirmNewPassword} onChange={set("confirmNewPassword")} />
          <FieldError messages={[mismatch, ...(fieldErrors(change.error, "ConfirmNewPassword") ?? [])]} />
        </div>
        <Button type="submit" variant="ink" disabled={change.isPending || !form.currentPassword || !form.newPassword}>
          {change.isPending && <Loader2 className="animate-spin" />}
          Change password
        </Button>
      </form>
    </Panel>
  );
}

function EmailForm({ current }: { current: string }) {
  const [newEmail, setNewEmail] = useState("");
  const change = useMutation({ mutationFn: () => api.account.changeEmail(newEmail.trim()) });

  return (
    <Panel title="Change email">
      {change.isSuccess ? (
        <Notice tone="success" title="Confirm your new address">
          We sent a confirmation link to {newEmail}. Your sign-in email stays {current} until you open it.
        </Notice>
      ) : (
        <form
          className="space-y-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate();
          }}
        >
          <ProblemAlert error={change.error} />
          <div className="space-y-2">
            <Label htmlFor="newEmail">New email address</Label>
            <Input id="newEmail" type="email" autoComplete="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            <FieldError messages={fieldErrors(change.error, "NewEmail")} />
          </div>
          <Button type="submit" variant="ink" disabled={change.isPending || !newEmail.trim()}>
            {change.isPending && <Loader2 className="animate-spin" />}
            Send confirmation link
          </Button>
        </form>
      )}
    </Panel>
  );
}
